// src/services/firebaseProjektService.js
// Firebase-Service für Projektverwaltung

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  addDoc,
  query,
  where,
  arrayUnion,
  arrayRemove,
  orderBy
} from "firebase/firestore";
import { db } from "../firebaseConfig";

// Rollen-Konstanten
export const ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  NIEDERLASSUNGSLEITER: 'niederlassungsleiter',
  OBJEKTLEITER: 'objektleiter',
  PROJEKTLEITER: 'projektleiter',
  SCHICHTLEITER: 'schichtleiter',
  TEAMLEITER: 'teamleiter',
  MITARBEITER: 'mitarbeiter'
};

// Rollen-Hierarchie für Berechtigungen
export const ROLE_HIERARCHY = {
  [ROLES.SUPERADMIN]: 8,
  [ROLES.ADMIN]: 7,
  [ROLES.NIEDERLASSUNGSLEITER]: 6,
  [ROLES.OBJEKTLEITER]: 5,
  [ROLES.PROJEKTLEITER]: 4,
  [ROLES.SCHICHTLEITER]: 3,
  [ROLES.TEAMLEITER]: 2,
  [ROLES.MITARBEITER]: 1
};

/**
 * Prüft, ob ein User eine bestimmte Rolle oder höher hat
 */
export const hasRoleOrHigher = (userRole, requiredRole) => {
  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
  return userLevel >= requiredLevel;
};

/**
 * Prüft, ob ein User zwischen Projekten wechseln darf
 */
export const canSwitchProjects = (userRole) => {
  return [
    ROLES.SUPERADMIN,
    ROLES.ADMIN,
    ROLES.NIEDERLASSUNGSLEITER,
    ROLES.OBJEKTLEITER,
    ROLES.PROJEKTLEITER
  ].includes(userRole);
};

/**
 * Sortiert Mitarbeiter nach ihrer hierarchischen Position (höchste zuerst)
 */
export const sortMitarbeiterByRole = (mitarbeiter) => {
  return mitarbeiter.sort((a, b) => {
    // Verwende die spezifische Projektrolle, falls vorhanden
    const aRole = a.projektRole || (a.projekte && a.projekte.length > 0 
      ? a.projekte.reduce((highest, projekt) => {
          const roleLevel = ROLE_HIERARCHY[projekt.role] || 0;
          const highestLevel = ROLE_HIERARCHY[highest] || 0;
          return roleLevel > highestLevel ? projekt.role : highest;
        }, a.projekte[0].role)
      : ROLES.MITARBEITER);
    
    const bRole = b.projektRole || (b.projekte && b.projekte.length > 0 
      ? b.projekte.reduce((highest, projekt) => {
          const roleLevel = ROLE_HIERARCHY[projekt.role] || 0;
          const highestLevel = ROLE_HIERARCHY[highest] || 0;
          return roleLevel > highestLevel ? projekt.role : highest;
        }, b.projekte[0].role)
      : ROLES.MITARBEITER);
    
    // Sortiere absteigend (höchste Rolle zuerst)
    const aLevel = ROLE_HIERARCHY[aRole] || 0;
    const bLevel = ROLE_HIERARCHY[bRole] || 0;
    
    if (aLevel !== bLevel) {
      return bLevel - aLevel; // Absteigend sortieren
    }
    
    // Bei gleicher Rolle alphabetisch nach Namen sortieren
    const aName = (a.name || a.username || '').toLowerCase();
    const bName = (b.name || b.username || '').toLowerCase();
    return aName.localeCompare(bName);
  });
};

/**
 * Alle Projekte laden
 */
export const getAllProjekte = async () => {
  try {
    console.log("Lade alle Projekte");
    const snapshot = await getDocs(collection(db, "projekte"));
    const projekte = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    console.log(`${projekte.length} Projekte geladen`);
    return projekte;
  } catch (error) {
    console.error("Fehler beim Laden der Projekte:", error);
    return [];
  }
};

/**
 * Ein spezifisches Projekt laden
 */
export const getProjektById = async (projektId) => {
  try {
    console.log(`Lade Projekt mit ID: ${projektId}`);
    const docRef = doc(db, "projekte", projektId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      console.log("Projekt nicht gefunden");
      return null;
    }
  } catch (error) {
    console.error("Fehler beim Laden des Projekts:", error);
    return null;
  }
};

/**
 * Neues Projekt erstellen
 */
export const createProjekt = async (projektData) => {
  try {
    console.log("Erstelle neues Projekt:", projektData.name);
    
    const projekt = {
      ...projektData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'aktiv'
    };
    
    const docRef = await addDoc(collection(db, "projekte"), projekt);
    console.log("Projekt erstellt mit ID:", docRef.id);
    
    return { id: docRef.id, ...projekt };
  } catch (error) {
    console.error("Fehler beim Erstellen des Projekts:", error);
    throw error;
  }
};

/**
 * Projekt aktualisieren
 */
export const updateProjekt = async (projektId, updateData) => {
  try {
    console.log(`Aktualisiere Projekt ${projektId}`);
    
    // Entferne undefined Werte aus updateData
    const cleanUpdateData = {};
    Object.keys(updateData).forEach(key => {
      if (updateData[key] !== undefined && updateData[key] !== null) {
        cleanUpdateData[key] = updateData[key];
      }
    });
    
    const updateFields = {
      ...cleanUpdateData,
      updatedAt: new Date().toISOString()
    };
    
    const docRef = doc(db, "projekte", projektId);
    await updateDoc(docRef, updateFields);
    
    console.log("Projekt erfolgreich aktualisiert");
    return true;
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Projekts:", error);
    throw error;
  }
};

/**
 * Projekt löschen
 */
export const deleteProjekt = async (projektId) => {
  try {
    console.log(`Lösche Projekt ${projektId}`);
    
    // Prüfen, ob noch Mitarbeiter dem Projekt zugeordnet sind
    const mitarbeiter = await getProjektMitarbeiter(projektId);
    if (mitarbeiter.length > 0) {
      throw new Error("Projekt kann nicht gelöscht werden, da noch Mitarbeiter zugeordnet sind");
    }
    
    const docRef = doc(db, "projekte", projektId);
    await deleteDoc(docRef);
    
    console.log("Projekt erfolgreich gelöscht");
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen des Projekts:", error);
    throw error;
  }
};

/**
 * Alle Mitarbeiter eines Projekts laden
 */
export const getProjektMitarbeiter = async (projektId) => {
  try {
    console.log(`Lade Mitarbeiter für Projekt ${projektId}`);
    
    const q = query(
      collection(db, "projektMitarbeiter"),
      where("projektId", "==", projektId)
    );
    
    const snapshot = await getDocs(q);
    const mitarbeiter = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`${mitarbeiter.length} Mitarbeiter für Projekt gefunden`);
    return mitarbeiter;
  } catch (error) {
    console.error("Fehler beim Laden der Projekt-Mitarbeiter:", error);
    return [];
  }
};

/**
 * Mitarbeiter zu einem Projekt zuordnen
 */
export const addMitarbeiterToProjekt = async (projektId, userId, role = ROLES.MITARBEITER) => {
  try {
    console.log(`Ordne Mitarbeiter ${userId} Projekt ${projektId} mit Rolle ${role} zu`);
    
    // Prüfen, ob Zuordnung bereits existiert
    const existingZuordnung = await getMitarbeiterProjektZuordnung(userId, projektId);
    if (existingZuordnung) {
      throw new Error("Mitarbeiter ist bereits diesem Projekt zugeordnet");
    }
    
    const zuordnung = {
      projektId,
      userId,
      role,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const docRef = await addDoc(collection(db, "projektMitarbeiter"), zuordnung);
    console.log("Mitarbeiter-Projekt-Zuordnung erstellt mit ID:", docRef.id);
    
    return { id: docRef.id, ...zuordnung };
  } catch (error) {
    console.error("Fehler beim Zuordnen des Mitarbeiters:", error);
    throw error;
  }
};

/**
 * Mitarbeiter-Rolle in einem Projekt aktualisieren
 */
export const updateMitarbeiterProjektRole = async (zuordnungId, newRole) => {
  try {
    console.log(`Aktualisiere Rolle für Zuordnung ${zuordnungId} auf ${newRole}`);
    
    const docRef = doc(db, "projektMitarbeiter", zuordnungId);
    await updateDoc(docRef, {
      role: newRole,
      updatedAt: new Date().toISOString()
    });
    
    console.log("Rolle erfolgreich aktualisiert");
    return true;
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Rolle:", error);
    throw error;
  }
};

/**
 * Mitarbeiter von einem Projekt entfernen
 */
export const removeMitarbeiterFromProjekt = async (zuordnungId) => {
  try {
    console.log(`Entferne Mitarbeiter-Projekt-Zuordnung ${zuordnungId}`);
    
    const docRef = doc(db, "projektMitarbeiter", zuordnungId);
    await deleteDoc(docRef);
    
    console.log("Mitarbeiter erfolgreich vom Projekt entfernt");
    return true;
  } catch (error) {
    console.error("Fehler beim Entfernen des Mitarbeiters:", error);
    throw error;
  }
};

/**
 * Spezifische Mitarbeiter-Projekt-Zuordnung laden
 */
export const getMitarbeiterProjektZuordnung = async (userId, projektId) => {
  try {
    // Prüfen, ob userId und projektId gültig sind
    if (!userId || !projektId) {
      console.log("Keine userId oder projektId angegeben");
      return null;
    }
    
    const q = query(
      collection(db, "projektMitarbeiter"),
      where("userId", "==", userId),
      where("projektId", "==", projektId)
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error("Fehler beim Laden der Mitarbeiter-Projekt-Zuordnung:", error);
    return null;
  }
};

/**
 * Alle Projekte eines Mitarbeiters laden
 */
export const getMitarbeiterProjekte = async (userId) => {
  try {
    // Prüfen, ob userId gültig ist
    if (!userId) {
      console.log("Keine userId angegeben, gebe leeres Array zurück");
      return [];
    }
    
    console.log(`Lade Projekte für Mitarbeiter ${userId}`);
    
    const q = query(
      collection(db, "projektMitarbeiter"),
      where("userId", "==", userId)
    );
    
    const snapshot = await getDocs(q);
    const zuordnungen = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Projektdetails für jede Zuordnung laden
    const projekte = await Promise.all(
      zuordnungen.map(async (zuordnung) => {
        const projekt = await getProjektById(zuordnung.projektId);
        return {
          ...zuordnung,
          projekt: projekt
        };
      })
    );
    
    console.log(`${projekte.length} Projekte für Mitarbeiter gefunden`);
    return projekte;
  } catch (error) {
    console.error("Fehler beim Laden der Mitarbeiter-Projekte:", error);
    return [];
  }
};

/**
 * Alle Mitarbeiter mit ihren Projektzuordnungen laden
 */
export const getAllMitarbeiterWithProjekte = async () => {
  try {
    console.log("Lade alle Mitarbeiter mit Projektzuordnungen");
    
    // Alle Mitarbeiter laden
    const usersSnapshot = await getDocs(collection(db, "users"));
    const users = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Alle Projektzuordnungen laden
    const zuordnungenSnapshot = await getDocs(collection(db, "projektMitarbeiter"));
    const zuordnungen = zuordnungenSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Alle Projekte laden
    const projekteSnapshot = await getDocs(collection(db, "projekte"));
    const projekte = projekteSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Mitarbeiter mit ihren Projekten zusammenführen
    const mitarbeiterWithProjekte = users.map(user => {
      const userZuordnungen = zuordnungen.filter(z => z.userId === user.id);
      const userProjekte = userZuordnungen.map(zuordnung => {
        const projekt = projekte.find(p => p.id === zuordnung.projektId);
        return {
          ...zuordnung,
          projekt: projekt
        };
      });
      
      return {
        ...user,
        projekte: userProjekte
      };
    });
    
    console.log(`${mitarbeiterWithProjekte.length} Mitarbeiter mit Projekten geladen`);
    return mitarbeiterWithProjekte;
  } catch (error) {
    console.error("Fehler beim Laden der Mitarbeiter mit Projekten:", error);
    return [];
  }
};

/**
 * Projekte basierend auf User-Rolle laden
 */
export const getProjekteForUser = async (userId, userRole) => {
  try {
    // Prüfen, ob userId gültig ist
    if (!userId) {
      console.log("Keine userId angegeben, gebe leeres Array zurück");
      return [];
    }
    
    console.log(`Lade Projekte für User ${userId} mit Rolle ${userRole}`);
    
    // Wenn User alle Projekte sehen darf
    if (canSwitchProjects(userRole)) {
      return await getAllProjekte();
    }
    
    // Sonst nur zugewiesene Projekte
    return await getMitarbeiterProjekte(userId);
  } catch (error) {
    console.error("Fehler beim Laden der Projekte für User:", error);
    return [];
  }
};
